import React, { memo, useCallback, useMemo, useState } from 'react'
import EditIcon from 'transfix-icons/build/EditIcon'
import { Card } from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { List } from 'transfix-ui/core'
import { useCarrierProfileContext } from 'apps/carrier/context/CarrierProfileContext'
import CarrierRelationshipListItem from './CarrierRelationshipListItem'
import { CarrierRelationshipsModal } from './CarrierRelationshipsModal'
import {
  groupCarrierRelationships,
  IGroupedCarrierRelationships,
} from './helpers/groupCarrierRelationships'

export const Managers = memo(() => {
  const { carrierApplication, error, loading } = useCarrierProfileContext()
  const { carrierRelationships } = carrierApplication ?? {}
  const { message: errorMessage } = error ?? {}
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  const {
    primaryCarrierManager,
    primaryCeRep,
    primarySalesRep,
    secondaryCarrierManagers,
    secondaryCeRep,
    secondarySalesReps,
  } = useMemo<IGroupedCarrierRelationships>(
    () => groupCarrierRelationships(carrierRelationships) ?? {},
    [carrierRelationships]
  )

  const hasManagers = Boolean(!loading && carrierRelationships && carrierRelationships.length > 0)

  const openModalHandler = useCallback(() => setModalOpen(true), [])
  const closeModalHandler = useCallback(() => setModalOpen(false), [])

  return (
    <Card>
      <div className='row pv4'>
        <h3 className='fl mb0 mt2'>Managers</h3>

        <div className='fr'>
          <IconButton
            Icon={EditIcon}
            color='tertiary'
            disabled={!carrierApplication || loading}
            onClick={openModalHandler}
            data-test='edit-managers-button'
            label='Edit Managers'
            size='large'
          />
        </div>
      </div>

      {loading && <Spinner position='relative' />}

      {errorMessage && (
        <InlineMessage message={errorMessage} type={INLINE_MESSAGE_VARIANTS.ALERT} />
      )}

      {carrierApplication && (
        <>
          {hasManagers ? (
            <List>
              {primaryCarrierManager && (
                <CarrierRelationshipListItem
                  title='Primary CM'
                  carrierRelationship={primaryCarrierManager}
                />
              )}
              {secondaryCarrierManagers?.map(r => (
                <CarrierRelationshipListItem
                  key={`secondary-cm-${r.user.id}`}
                  title='Secondary CM'
                  carrierRelationship={r}
                />
              ))}
              {primaryCeRep && (
                <CarrierRelationshipListItem
                  title='Primary CE'
                  carrierRelationship={primaryCeRep}
                />
              )}
              {secondaryCeRep && (
                <CarrierRelationshipListItem
                  title='Secondary CE'
                  carrierRelationship={secondaryCeRep}
                />
              )}
              {primarySalesRep && (
                <CarrierRelationshipListItem
                  title='Primary CS'
                  carrierRelationship={primarySalesRep}
                />
              )}
              {secondarySalesReps?.map(r => (
                <CarrierRelationshipListItem
                  key={`secondary-cm-${r.user.id}`}
                  title='Secondary CS'
                  carrierRelationship={r}
                />
              ))}
            </List>
          ) : (
            <p>No managers.</p>
          )}

          {carrierApplication && modalOpen && (
            <CarrierRelationshipsModal
              carrierApplication={carrierApplication}
              open={modalOpen}
              onClose={closeModalHandler}
            />
          )}
        </>
      )}
    </Card>
  )
})
